import React, { useMemo, useState } from "react";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import ListCard from "../components/ListCard";
import ItemDialog from "../components/ItemDialog";
import { removeItem, upsertItem } from "../../hooks/useTrustCenterAdmin";
import { includesQuery } from "../../../Organization/CodeSecurity/codeSecurityShared";

// Table + add/edit dialog for plain lists (FAQ).
const SimpleListTab = ({ config, items, onChange }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  // The row waiting for "Remove?" to be confirmed (row button).
  const [removing, setRemoving] = useState(null);

  const rows = useMemo(
    () =>
      items.filter((item) =>
        includesQuery(search, ...config.fields.map((f) => item[f.name]))
      ),
    [items, search, config.fields]
  );

  const columns = [
    ...config.columns,
    { label: "Actions", field: "actions", width: "12%", align: "center" },
  ];

  const renderCell = (row, column) =>
    column.field === "actions" ? (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        <Tooltip title="Remove" arrow>
          <IconButton
            size="small"
            aria-label={`Remove ${config.noun}`}
            onClick={() => setRemoving(row)}
            sx={{ color: theme.customColors.status.error.text }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={`Edit ${config.noun}`} arrow>
          <IconButton
            size="small"
            aria-label={`Edit ${config.noun}`}
            onClick={() => setEditing(row)}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    ) : (
      <Typography
        variant="body2"
        sx={
          column.clamp
            ? {
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                // Long words or links wrap instead of widening the table.
                overflowWrap: "anywhere",
              }
            : { overflowWrap: "anywhere" }
        }
      >
        {row[column.field] || "—"}
      </Typography>
    );

  return (
    <>
      <ListCard
        title={config.title}
        subtitle={config.subtitle}
        addLabel={`Add ${config.noun}`}
        onAdd={() => setEditing(config.empty)}
        searchQuery={search}
        onSearchChange={setSearch}
        onClearFilters={() => setSearch("")}
        countNoun={config.noun}
        columns={columns}
        rows={rows}
        renderCell={renderCell}
        emptyText={config.emptyText}
      />
      <ItemDialog
        open={!!editing}
        title={editing?.id ? `Edit ${config.noun}` : `Add ${config.noun}`}
        fields={config.fields}
        initialValues={editing}
        emptyValues={config.empty}
        onClose={() => setEditing(null)}
        onSave={(values) => onChange(upsertItem(items, values))}
        onRemove={(id) => onChange(removeItem(items, id))}
        removeHelperText={config.removeHelperText}
      />
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          onChange(removeItem(items, removing.id));
          setRemoving(null);
        }}
        heading={`Remove this ${config.noun}?`}
        content={config.removeHelperText}
        buttonText="Remove"
      />
    </>
  );
};

export const FAQ_CONFIG = {
  title: "FAQ",
  subtitle:
    "Answer the questions buyers ask most, so they don't have to email you.",
  noun: "question",
  empty: { question: "", answer: "" },
  fields: [
    { name: "question", label: "Question", type: "text", required: true },
    { name: "answer", label: "Answer", type: "textarea", required: true },
  ],
  columns: [
    { label: "Question", field: "question", width: "35%" },
    { label: "Answer", field: "answer", width: "55%", clamp: true },
  ],
  emptyText: "No questions yet. Start with “Where is my data stored?”",
  removeHelperText: "Remove this question from your Trust Center.",
};

export default SimpleListTab;
